import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { authService } from '@/features/auth/services/authService'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { supabase } from '@/shared/lib/supabase/client'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
})

function AuthCallbackPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const { setAuthFromOAuth } = useAuthStore()

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Supabase detects tokens from URL hash automatically (detectSessionInUrl: true)
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession()

        if (sessionError || !session) {
          toast.error(extractErrorMessage(sessionError))
          navigate({ to: '/login', search: { error: 'oauth_failed' } })
          return
        }

        // Sync OAuth user into local system and get LoginResponse
        const result = await authService.syncOAuthUser({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        })

        // Update auth store with the result
        setAuthFromOAuth(result)

        navigate({ to: '/dashboard' })
      } catch (err) {
        toast.error(extractErrorMessage(err))
        setError('Sign in failed. Please try again.')
        setTimeout(() => {
          navigate({ to: '/login' })
        }, 2000)
      }
    }

    handleCallback()
  }, [navigate, setAuthFromOAuth])

  if (error) {
    return (
      <div className='flex min-h-svh items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-500 text-sm mb-2'>{error}</p>
          <p className='text-muted-foreground text-sm'>
            Redirecting to login...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex min-h-svh items-center justify-center'>
      <div className='flex flex-col items-center gap-3'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
        <p className='text-muted-foreground text-sm'>Completing sign in...</p>
      </div>
    </div>
  )
}
